import type { Exercise } from "../types";

/**
 * Nivel Aprendiz — Cap. I–IV método chileno.
 * En camino (`pathMode`), `practice`/`sequence` validan con micrófono (pitch).
 */
export const aprendizExercises: Exercise[] = [
  {
    id: "ap-01",
    levelId: "aprendiz",
    order: 1,
    title: "Cuerdas al Aire",
    subtitle: "Toca Mi, La y Re graves con el micrófono",
    category: "notas",
    difficulty: "facil",
    type: "practice",
    methodRef: "Cap. I - Partes de la Guitarra & Afinacion",
    description:
      "Conoce la voz de las tres cuerdas graves: Mi (E2), La (A2) y Re (D3). Toca cada cuerda al aire; el micrófono valida la nota real.",
    instructions: [
      "Afina la guitarra antes de empezar.",
      "Cuando se ilumine una cuerda, tócala al aire (sin pisar).",
      "Espera a que el micrófono confirme la nota para avanzar.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Toca la 6ª cuerda al aire — Mi grave (E2)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2", "Mi"],
        hint: "Cuerda más gruesa, arriba del mástil",
      },
      {
        id: 2,
        instruction: "Toca la 5ª cuerda al aire — La (A2)",
        positions: [{ string: 5, fret: 0 }],
        answers: ["A2", "La"],
        hint: "Segunda cuerda desde arriba",
      },
      {
        id: 3,
        instruction: "Toca la 4ª cuerda al aire — Re (D3)",
        positions: [{ string: 4, fret: 0 }],
        answers: ["D3", "Re"],
        hint: "Tercera cuerda, en el medio del bloque grave",
      },
    ],
    professorHint:
      'Frase mnemotécnica: "Esto Es Un…" → Mi–La–Re. Empieza solo con las graves.',
    professorNote:
      "El método chileno dedica el Capítulo I a estos conocimientos preliminares. Sin una afinación interna sólida, todo lo demás será inestable.",
    timeEstimate: 4,
    stringsNeeded: [4, 5, 6],
    fretRange: [0, 0],
  },
  {
    id: "ap-01b",
    levelId: "aprendiz",
    order: 2,
    title: "Alternancia al Aire",
    subtitle: "Cambia de cuerda grave sin pisar: E–A–E–D",
    category: "digitacion",
    difficulty: "facil",
    type: "practice",
    methodRef: "Cap. I–II - Pulsación al aire",
    description:
      "Entrena la mano derecha alternando cuerdas al aire. Secuencia: 6ª → 5ª → 6ª → 4ª. El micrófono avanza solo cuando escuchas la nota correcta.",
    instructions: [
      "No pises ningún traste: todo es al aire.",
      "Sigue el orden: Mi grave → La → Mi grave → Re.",
      "Toca limpio; espera la confirmación antes del siguiente paso.",
    ],
    steps: [
      {
        id: 1,
        instruction: "6ª al aire — Mi (E2)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
        hint: "Cuerda más grave",
      },
      {
        id: 2,
        instruction: "5ª al aire — La (A2)",
        positions: [{ string: 5, fret: 0 }],
        answers: ["A2"],
        hint: "Salta a la cuerda de al lado",
      },
      {
        id: 3,
        instruction: "Vuelve a la 6ª — Mi (E2)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
        hint: "Misma cuerda que el paso 1",
      },
      {
        id: 4,
        instruction: "4ª al aire — Re (D3)",
        positions: [{ string: 4, fret: 0 }],
        answers: ["D3"],
        hint: "Una cuerda más hacia el centro",
      },
    ],
    professorHint:
      "El pulgar puede hacer toda la secuencia. Mantén la muñeca relajada y el movimiento pequeño.",
    professorNote:
      "La alternancia al aire prepara el acompañamiento: el oído y la mano aprenden a cambiar de cuerda sin mirar.",
    timeEstimate: 5,
    stringsNeeded: [4, 5, 6],
    fretRange: [0, 0],
  },
  {
    id: "ap-01c",
    levelId: "aprendiz",
    order: 3,
    title: "Agudos al Aire",
    subtitle: "Control en cuerdas delgadas: G–B–E–B",
    category: "notas",
    difficulty: "facil",
    type: "practice",
    methodRef: "Cap. I - Cuerdas agudas al aire",
    description:
      "Las cuerdas agudas piden menos fuerza y más precisión. Secuencia al aire: Sol (G3) → Si (B3) → Mi aguda (E4) → Si (B3). El micrófono valida cada nota.",
    instructions: [
      "Todo al aire: no pises trastes.",
      "Orden: 3ª → 2ª → 1ª → 2ª.",
      "Toca suave; las delgadas suenan fácil y se saturan el micrófono si golpeas fuerte.",
    ],
    steps: [
      {
        id: 1,
        instruction: "3ª al aire — Sol (G3)",
        positions: [{ string: 3, fret: 0 }],
        answers: ["G3", "Sol"],
        hint: "Tercera cuerda desde abajo",
      },
      {
        id: 2,
        instruction: "2ª al aire — Si (B3)",
        positions: [{ string: 2, fret: 0 }],
        answers: ["B3", "Si"],
        hint: "Segunda cuerda desde abajo",
      },
      {
        id: 3,
        instruction: "1ª al aire — Mi aguda (E4)",
        positions: [{ string: 1, fret: 0 }],
        answers: ["E4", "Mi"],
        hint: "La cuerda más delgada",
      },
      {
        id: 4,
        instruction: "Vuelve a la 2ª — Si (B3)",
        positions: [{ string: 2, fret: 0 }],
        answers: ["B3", "Si"],
        hint: "Misma cuerda que el paso 2",
      },
    ],
    professorHint:
      "Usa la yema o la uña con poco recorrido. El volumen no importa: importa el tono limpio.",
    professorNote:
      "Dominar las agudas al aire equilibra el mapa de las 6 cuerdas después de las graves (ap-01 / ap-01b).",
    timeEstimate: 4,
    stringsNeeded: [1, 2, 3],
    fretRange: [0, 0],
  },
  {
    id: "ap-01d",
    levelId: "aprendiz",
    order: 4,
    title: "Saltos de Cuerda",
    subtitle: "Precisión espacial (string skipping): E–D–A–G",
    category: "digitacion",
    difficulty: "facil",
    type: "practice",
    methodRef: "Cap. I–II - Pulsación saltando cuerdas",
    description:
      "Entrena saltos no consecutivos: 6ª → 4ª → 5ª → 3ª. La mano derecha aprende a apuntar sin arrastrar cuerdas vecinas.",
    instructions: [
      "Todo al aire.",
      "Orden: Mi grave → Re → La → Sol.",
      "Si suenan dos cuerdas a la vez, repite el paso con menos amplitud.",
    ],
    steps: [
      {
        id: 1,
        instruction: "6ª al aire — Mi (E2)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
        hint: "Cuerda más grave",
      },
      {
        id: 2,
        instruction: "Salta a la 4ª — Re (D3)",
        positions: [{ string: 4, fret: 0 }],
        answers: ["D3"],
        hint: "Saltas por encima de la 5ª",
      },
      {
        id: 3,
        instruction: "Ahora la 5ª — La (A2)",
        positions: [{ string: 5, fret: 0 }],
        answers: ["A2"],
        hint: "Entre la 6ª y la 4ª",
      },
      {
        id: 4,
        instruction: "Salta a la 3ª — Sol (G3)",
        positions: [{ string: 3, fret: 0 }],
        answers: ["G3"],
        hint: "Entrada a las agudas",
      },
    ],
    professorHint:
      "Visualiza el salto antes de tocar. El pulgar o la púa debe viajar en arco corto, no en línea recta grande.",
    professorNote:
      "Los saltos de cuerda son la base del acompañamiento y de arpegios limpios.",
    timeEstimate: 5,
    stringsNeeded: [3, 4, 5, 6],
    fretRange: [0, 0],
  },
  {
    id: "ap-02",
    levelId: "aprendiz",
    order: 5,
    title: "Semitonos Vecinos",
    subtitle: "La distancia más corta entre dos notas",
    category: "teoria",
    difficulty: "facil",
    type: "click",
    methodRef: "Cap. II - Digitación, Ej. 3 al 9",
    description:
      "En el mástil, cada traste es un semitono. Este ejercicio entrena tu cerebro para ver el mástil como un mapa continuo de semitonos, no como trastes aislados.",
    instructions: [
      "Se te indicará una nota de partida.",
      "Calcula el semitono siguiente (un traste arriba) o anterior (un traste abajo).",
      "Valida tocando la nota en la guitarra cuando el micrófono esté activo, o siguiendo la guía del mástil.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Partiendo de Mi (cuerda 6, traste 0), sube un semitono",
        positions: [{ string: 6, fret: 1 }],
        answers: ["Fa"],
        hint: "Un traste arriba de Mi es Fa",
      },
      {
        id: 2,
        instruction: "Partiendo de La (cuerda 5, traste 0), sube dos semitonos",
        positions: [{ string: 5, fret: 2 }],
        answers: ["Si"],
        hint: "Dos trastes arriba de La",
      },
      {
        id: 3,
        instruction: "Partiendo de Sol (cuerda 3, traste 0), sube un semitono",
        positions: [{ string: 3, fret: 1 }],
        answers: ["Sol#"],
        hint: "Sostenido = subir un semitono",
      },
      {
        id: 4,
        instruction: "Partiendo de Si (cuerda 2, traste 0), sube un semitono",
        positions: [{ string: 2, fret: 1 }],
        answers: ["Do"],
        hint: "Do está justo después de Si",
      },
      {
        id: 5,
        instruction: "Partiendo de Do (cuerda 2, traste 1), baja un semitono",
        positions: [{ string: 2, fret: 0 }],
        answers: ["Si"],
        hint: "Bemol = bajar un semitono",
      },
    ],
    professorHint:
      "Piensa en el mástil como una escalera: cada traste es un escalón. Subes = sostenido (#), bajas = bemol (b).",
    professorNote:
      "El método chileno introduce la digitación en el Capítulo II con ejercicios preliminares. Aquí los convertimos en un mapa interactivo del mástil.",
    timeEstimate: 4,
    stringsNeeded: [1, 2, 3, 4, 5, 6],
    fretRange: [0, 3],
  },
  {
    id: "ap-02b",
    levelId: "aprendiz",
    order: 6,
    title: "Aire vs Traste",
    subtitle: "Intercambia cuerda al aire y nota pisada en la 6ª",
    category: "notas",
    difficulty: "facil",
    type: "practice",
    methodRef: "Cap. II - Digitación básica en una cuerda",
    description:
      "Siente la diferencia entre al aire y pisado en la 6ª cuerda: E2 → F2 → E2 → G2. El micrófono valida cada nota.",
    instructions: [
      "Paso al aire: no pises. Paso con traste: usa el dedo indicado.",
      "Secuencia: E2 (aire) → F2 (traste 1) → E2 (aire) → G2 (traste 3).",
      "Suelta el dedo al volver al aire para que suene limpio.",
    ],
    steps: [
      {
        id: 1,
        instruction: "6ª al aire — Mi (E2)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
        hint: "Sin dedos en el mástil",
      },
      {
        id: 2,
        instruction: "6ª traste 1 — Fa (F2) con dedo 1",
        positions: [{ string: 6, fret: 1 }],
        answers: ["F2", "Fa"],
        hint: "Un semitono arriba del al aire",
      },
      {
        id: 3,
        instruction: "Vuelve al aire — Mi (E2)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
        hint: "Levanta el dedo y pulsa de nuevo",
      },
      {
        id: 4,
        instruction: "6ª traste 3 — Sol (G2) con dedo 3",
        positions: [{ string: 6, fret: 3 }],
        answers: ["G2", "Sol"],
        hint: "Tres trastes arriba del al aire",
      },
    ],
    professorHint:
      "Dedo 1 en traste 1, dedo 3 en traste 3. No uses el mismo dedo para distancias distintas.",
    professorNote:
      "Alternar aire y traste fija la relación oído–mano: misma cuerda, altura distinta.",
    timeEstimate: 5,
    stringsNeeded: [6],
    fretRange: [0, 3],
  },
  {
    id: "ap-02c",
    levelId: "aprendiz",
    order: 7,
    title: "Caminata Cromática Base",
    subtitle: "Dedos 1–2–3 sin soltar: E–F–F#–G en 6ª",
    category: "digitacion",
    difficulty: "facil",
    type: "sequence",
    methodRef: "Cap. II - Cromatismo en una cuerda",
    description:
      "Sube cromáticamente por la 6ª: al aire → traste 1 → 2 → 3. Usa dedos 1, 2 y 3 en secuencia sin soltar el apoyo; el micrófono valida cada nota por separado.",
    instructions: [
      "Dedo 1 en traste 1, dedo 2 en traste 2, dedo 3 en traste 3.",
      "No sueltes los dedos ya apoyados al avanzar.",
      "Toca una nota por paso; espera la confirmación del mic.",
    ],
    steps: [
      {
        id: 1,
        instruction: "6ª al aire — Mi (E2)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
        hint: "Sin pisar",
      },
      {
        id: 2,
        instruction: "Traste 1 — Fa (F2) con dedo 1",
        positions: [{ string: 6, fret: 1 }],
        answers: ["F2"],
        hint: "Un semitono",
      },
      {
        id: 3,
        instruction: "Traste 2 — Fa# (F#2) con dedo 2",
        positions: [{ string: 6, fret: 2 }],
        answers: ["F#2"],
        hint: "Siguiente semitono",
      },
      {
        id: 4,
        instruction: "Traste 3 — Sol (G2) con dedo 3",
        positions: [{ string: 6, fret: 3 }],
        answers: ["G2"],
        hint: "Cierra la caminata",
      },
    ],
    professorHint:
      "No uses el mismo dedo para subir: 1 → 2 → 3 fija el mapa de dedos fijos.",
    professorNote:
      "La caminata cromática en la 6ª prepara digitación con apoyo y escalas mayores.",
    timeEstimate: 4,
    stringsNeeded: [6],
    fretRange: [0, 3],
  },
  {
    id: "ap-02d",
    levelId: "aprendiz",
    order: 8,
    title: "Alternancia 1 y 3",
    subtitle: "Independencia de dedos impares: F↔G en 6ª",
    category: "digitacion",
    difficulty: "facil",
    type: "sequence",
    methodRef: "Cap. II–III - Independencia digital",
    description:
      "Alterna dedo 1 (traste 1, Fa) y dedo 3 (traste 3, Sol) en la 6ª cuerda. Entrena independencia de dedos impares sin usar el dedo 2.",
    instructions: [
      "Dedo 1 fijo en traste 1; dedo 3 en traste 3.",
      "Secuencia: F2 → G2 → F2 → G2.",
      "El dedo 2 puede quedar relajado cerca del mástil, sin pisar.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Traste 1 — Fa (F2) con dedo 1",
        positions: [{ string: 6, fret: 1 }],
        answers: ["F2", "Fa"],
        hint: "Dedo índice",
      },
      {
        id: 2,
        instruction: "Traste 3 — Sol (G2) con dedo 3",
        positions: [{ string: 6, fret: 3 }],
        answers: ["G2", "Sol"],
        hint: "Dedo anular; salta el traste 2",
      },
      {
        id: 3,
        instruction: "Vuelve al traste 1 — Fa (F2)",
        positions: [{ string: 6, fret: 1 }],
        answers: ["F2", "Fa"],
        hint: "Mantén dedo 3 cerca si puedes",
      },
      {
        id: 4,
        instruction: "Otra vez traste 3 — Sol (G2)",
        positions: [{ string: 6, fret: 3 }],
        answers: ["G2", "Sol"],
        hint: "Cierra la alternancia",
      },
    ],
    professorHint:
      "No arrastres el dedo 1 hasta el traste 3: cada dedo vive en su traste.",
    professorNote:
      "La independencia 1–3 prepara patrones de escala y apoyos sin colapsar la mano.",
    timeEstimate: 4,
    stringsNeeded: [6],
    fretRange: [1, 3],
  },
  {
    id: "ap-03",
    levelId: "aprendiz",
    order: 9,
    title: "Digitación con Apoyo",
    subtitle: "El secreto de la mano izquierda: dedos firmes, mudos rápidos",
    category: "digitacion",
    difficulty: "facil",
    type: "sequence",
    methodRef: "Cap. II - Pulsación con apoyo",
    description:
      'La técnica del "apoyo" consiste en dejar el dedo apoyado en el traste después de pulsar, mientras el siguiente dedo cae. Es la base de toda digitación profesional.',
    instructions: [
      "Observa la secuencia de digitación en el mástil.",
      "Toca cada nota pedida; el micrófono valida.",
      "Cada nota se mantiene (apoyo) hasta que la siguiente suena.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Pulsa el Mi grave (cuerda 6, traste 0) y mantenlo",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
      },
      {
        id: 2,
        instruction: "Añade Fa (cuerda 6, traste 1) con dedo 1 — toca Fa",
        positions: [{ string: 6, fret: 1 }],
        answers: ["F2"],
        hint: "Mantén el apoyo mental; suena la nota nueva",
      },
      {
        id: 3,
        instruction: "Añade Fa# (cuerda 6, traste 2) con dedo 2 — toca Fa#",
        positions: [{ string: 6, fret: 2 }],
        answers: ["F#2"],
      },
      {
        id: 4,
        instruction: "Añade Sol (cuerda 6, traste 3) con dedo 3 — toca Sol",
        positions: [{ string: 6, fret: 3 }],
        answers: ["G2"],
      },
      {
        id: 5,
        instruction: "Desciende: toca Sol#… espera — toca Fa# (traste 2)",
        positions: [{ string: 6, fret: 2 }],
        answers: ["F#2"],
        hint: "Bajando un traste",
      },
      {
        id: 6,
        instruction: "Sigue bajando: Fa (traste 1)",
        positions: [{ string: 6, fret: 1 }],
        answers: ["F2"],
      },
      {
        id: 7,
        instruction: "Cierra en Mi al aire (traste 0)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
      },
    ],
    professorHint:
      'El "apoyo" significa que cada dedo se queda en su traste mientras los demás se posicionan. No levantes los dedos hasta que sea necesario.',
    professorNote:
      "El método chileno insiste en la pulsación con apoyo como técnica fundamental. Es lo que separa a un principiante de un estudiante serio.",
    timeEstimate: 5,
    stringsNeeded: [6],
    fretRange: [0, 3],
  },
  {
    id: "ap-04",
    levelId: "aprendiz",
    order: 10,
    title: "Dedos Fijos",
    subtitle: "Cada dedo tiene su traste asignado: 1-2-3-4",
    category: "digitacion",
    difficulty: "facil",
    type: "sequence",
    methodRef: "Cap. III - Ejercicios de mano izquierda con dedos fijos",
    description:
      "El método asigna un dedo por traste: dedo 1 = traste 1, dedo 2 = traste 2, etc. Este ejercicio automatiza esa relación sin pensar.",
    instructions: [
      "Cada dedo se queda en su traste asignado.",
      "Toca la nota resaltada; el micrófono confirma.",
      "No muevas los dedos de su traste asignado.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Dedo 1 en traste 1: pulsa cuerda 6 (Fa)",
        positions: [{ string: 6, fret: 1 }],
        answers: ["F2"],
      },
      {
        id: 2,
        instruction: "Dedo 2 en traste 2: pulsa cuerda 6 (Fa#)",
        positions: [{ string: 6, fret: 2 }],
        answers: ["F#2"],
      },
      {
        id: 3,
        instruction: "Dedo 3 en traste 3: pulsa cuerda 6 (Sol)",
        positions: [{ string: 6, fret: 3 }],
        answers: ["G2"],
      },
      {
        id: 4,
        instruction: "Dedo 4 en traste 4: pulsa cuerda 6 (Sol#)",
        positions: [{ string: 6, fret: 4 }],
        answers: ["G#2"],
      },
      {
        id: 5,
        instruction: "Desciende: traste 3 (Sol)",
        positions: [{ string: 6, fret: 3 }],
        answers: ["G2"],
      },
      {
        id: 6,
        instruction: "Desciende: traste 2 (Fa#)",
        positions: [{ string: 6, fret: 2 }],
        answers: ["F#2"],
      },
      {
        id: 7,
        instruction: "Desciende: traste 1 (Fa)",
        positions: [{ string: 6, fret: 1 }],
        answers: ["F2"],
      },
    ],
    professorHint:
      "Los dedos fijos son la regla de oro: dedo 1 para traste 1, dedo 2 para traste 2, etc. Solo cuando domines esto podrás hacer excepciones.",
    professorNote:
      "El Capítulo III del método chileno se dedica enteramente a los dedos fijos. Es aburrido pero esencial: aquí se forja la independencia digital.",
    timeEstimate: 5,
    stringsNeeded: [6],
    fretRange: [0, 4],
  },
  {
    id: "ap-05",
    levelId: "aprendiz",
    order: 11,
    title: "Articulación del Pulgar",
    subtitle: "El pulgar es el director de orquesta de la mano derecha",
    category: "digitacion",
    difficulty: "medio",
    type: "practice",
    methodRef: "Cap. IV - Articulación del pulgar (Ej. 11 y 12)",
    description:
      "En la guitarra chilena, el pulgar no solo toca las cuerdas graves: es el motor del acompañamiento. Aprende a articularlo correctamente para rasgueos y punteos.",
    instructions: [
      "El pulgar toca las cuerdas 4, 5 y 6 en secuencia.",
      "Practica el movimiento descendente del pulgar.",
      "Mantén i, m, a apoyados en las cuerdas 1, 2, 3.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Pulgar toca cuerda 6 (Mi grave) hacia abajo",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
      },
      {
        id: 2,
        instruction: "Pulgar toca cuerda 5 (La) hacia abajo",
        positions: [{ string: 5, fret: 0 }],
        answers: ["A2"],
      },
      {
        id: 3,
        instruction: "Pulgar toca cuerda 4 (Re) hacia abajo",
        positions: [{ string: 4, fret: 0 }],
        answers: ["D3"],
      },
      {
        id: 4,
        instruction: "Invierte: cuerda 4 (Re)",
        positions: [{ string: 4, fret: 0 }],
        answers: ["D3"],
      },
      {
        id: 5,
        instruction: "Sigue: cuerda 5 (La)",
        positions: [{ string: 5, fret: 0 }],
        answers: ["A2"],
      },
      {
        id: 6,
        instruction: "Cierra: cuerda 6 (Mi)",
        positions: [{ string: 6, fret: 0 }],
        answers: ["E2"],
      },
    ],
    professorHint:
      'El pulgar "articula" cuando sus dos falanges trabajan juntas. No muevas todo el brazo: el movimiento es desde la articulación del pulgar.',
    professorNote:
      "El Capítulo IV del método dedica dos ejercicios exclusivos a la articulación del pulgar. Es la técnica que diferencia al acompañamiento folclórico de otros estilos.",
    timeEstimate: 5,
    stringsNeeded: [4, 5, 6],
    fretRange: [0, 0],
  },
];
